import { useState } from 'react';
import { useAuth } from '../state/auth';
import { useToast } from '../state/toast';
import { AlertIcon } from '../ui/icons';
import ConfirmDialog from '../ui/ConfirmDialog';

export function EraseUserRangeForm() {
  const token = useAuth((s) => s.token)!;
  const pushToast = useToast((s) => s.push);
  const [form, setForm] = useState({ board_id: '', user_id: '', from: '', to: '' });
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  async function execute() {
    setBusy(true);
    setConfirming(false);
    try {
      const res = await fetch('/api/admin/erase/user-range', {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
        body: JSON.stringify({
          board_id: parseInt(form.board_id, 10),
          user_id: form.user_id,
          from: new Date(form.from).toISOString(),
          to: new Date(form.to).toISOString(),
        }),
      });
      if (!res.ok) {
        let msg = `http_${res.status}`;
        try {
          const errBody = await res.json();
          if (errBody?.error) msg = errBody.error;
        } catch {
          // body 不是 JSON，使用 http_status
        }
        pushToast('error', `擦除失败：${msg}`, 5000);
        return;
      }
      const body = await res.json();
      const n = typeof body?.erased_count === 'number' ? body.erased_count : 0;
      pushToast('success', `已擦除 ${n} 条笔画`, 4000);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'network_error';
      pushToast('error', `擦除失败：${msg}`, 5000);
    } finally {
      setBusy(false);
    }
  }

  const valid = !!(form.board_id && form.user_id && form.from && form.to);

  return (
    <section className="bg-white rounded-xl border border-neutral-200 p-5">
      <div className="flex items-center gap-2 mb-1">
        <AlertIcon size={16} />
        <h3 className="text-[15px] font-semibold tracking-tight">按用户 + 时段擦除</h3>
      </div>
      <p className="text-xs text-neutral-500 mb-4">
        擦除指定用户在时间范围内的笔画。此操作会写入审计日志并可回滚。
      </p>
      <div className="flex flex-wrap gap-2 items-end">
        <LabeledInput label="Board ID" value={form.board_id} onChange={(v) => setForm({ ...form, board_id: v })} />
        <LabeledInput label="User ID" value={form.user_id} onChange={(v) => setForm({ ...form, user_id: v })} />
        <LabeledInput
          label="开始"
          type="datetime-local"
          value={form.from}
          onChange={(v) => setForm({ ...form, from: v })}
        />
        <LabeledInput
          label="结束"
          type="datetime-local"
          value={form.to}
          onChange={(v) => setForm({ ...form, to: v })}
        />
        <button
          type="button"
          disabled={!valid || busy}
          className="h-9 px-3.5 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-neutral-300 disabled:cursor-not-allowed"
          onClick={() => setConfirming(true)}
        >
          {busy ? '执行中…' : '执行擦除'}
        </button>
      </div>

      <ConfirmDialog
        open={confirming}
        title="确认擦除？"
        destructive
        confirmLabel="执行擦除"
        body={
          <>
            将擦除 board <b>{form.board_id}</b> 中 user <b>{form.user_id}</b> 自{' '}
            <b>{form.from || '—'}</b> 至 <b>{form.to || '—'}</b> 之间的全部笔画。
            <br />
            此操作会写入审计日志并可回滚。
          </>
        }
        onCancel={() => setConfirming(false)}
        onConfirm={execute}
      />
    </section>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium uppercase tracking-wider text-neutral-500">
        {label}
      </span>
      <input
        type={type}
        className="h-9 px-2.5 rounded-lg border border-neutral-200 bg-white text-sm outline-none focus:border-neutral-900"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}
